import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, Chip, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ResponsiveDialog from "../components/ResponsiveDialog";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import { adminPageSx, contentCardSx } from "../utils/adminResponsive";
import { Colors } from "../utils/colors";

const staffName = (admin) => [admin?.first_name, admin?.last_name].filter(Boolean).join(" ") || admin?.email || "Staff";

export default function Messages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentId = searchParams.get("paymentId");
  const [box, setBox] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(false);
  const [form, setForm] = useState({ recipient_admin_id: "", subject: "", message: "" });
  const [transaction, setTransaction] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async (nextBox = box) => {
    setLoading(true);
    const response = await adminFetch(`/api/admin/messages?box=${nextBox}`);
    if (response?.status) setMessages(response.messages || []);
    else setNotice(response?.message || "Unable to load internal messages");
    setLoading(false);
  }, [box]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminFetch("/api/admin/messages/recipients").then((response) => {
      if (response?.status) setRecipients(response.recipients || []);
    });
  }, []);
  useEffect(() => {
    if (!/^\d+$/.test(paymentId || "")) return;
    adminFetch(`/api/admin/messages/transaction/${paymentId}/context`).then((response) => {
      if (response?.status) setTransaction({ ...response.data, message: "" });
      else setNotice(response?.message || "Unable to load transaction");
    });
  }, [paymentId]);

  const sendInternal = async () => {
    const response = await adminFetch("/api/admin/messages", { method: "POST", body: JSON.stringify(form) });
    if (!response?.status) return setNotice(response?.message || "Unable to send message");
    setCompose(false); setForm({ recipient_admin_id: "", subject: "", message: "" }); setNotice(response.message);
    setBox("sent"); await load("sent");
  };

  const sendCustomerEmail = async () => {
    const response = await adminFetch(`/api/admin/messages/transaction/${transaction.payment_id}`, {
      method: "POST", body: JSON.stringify({ message: transaction.message })
    });
    if (!response?.status) return setNotice(response?.message || "Unable to queue customer email");
    setTransaction(null); setSearchParams({}); setNotice(response.message);
  };

  const openMessage = async (id) => {
    const response = await adminFetch(`/api/admin/messages/${id}`);
    if (response?.status) { setSelected(response.message); if (box === "inbox") await load(); }
  };

  const columns = [
    { key: "staff", label: box === "inbox" ? "From" : "To", render: (row) => staffName(box === "inbox" ? row.Sender : row.Recipient) },
    { key: "role", label: "Role", render: (row) => <Chip size="small" label={(box === "inbox" ? row.Sender : row.Recipient)?.role || "staff"} /> },
    { key: "subject", label: "Subject", render: (row) => row.subject },
    { key: "status", label: "Status", render: (row) => box === "sent" ? "Sent" : row.read_at ? "Read" : "Unread" },
    { key: "created", label: "Date", render: (row) => formatAdminDateTime(row.created_at) },
    { key: "actions", label: "Action", isActions: true, render: (row) => <Button onClick={() => openMessage(row.id)}>View</Button> }
  ];

  return <Box sx={adminPageSx}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
      <Box><Typography variant="h5" fontWeight={700} color={Colors.accent}>Internal Staff Messaging</Typography>
        <Typography color="text.secondary">Messages are visible only to authorized Admin staff.</Typography></Box>
      <Button variant="contained" onClick={() => setCompose(true)}>New Message</Button>
    </Stack>
    {notice && <Alert sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}
    <Card sx={{ p: 2, ...contentCardSx }}>
      <Tabs value={box} onChange={(_, value) => setBox(value)}><Tab value="inbox" label="Inbox" /><Tab value="sent" label="Sent" /></Tabs>
      <Box sx={{ position: "relative", minHeight: 320, mt: 2 }}><ResponsiveDataTable columns={columns} rows={messages}
        loading={loading} minWidth={850} emptyMessage={`No ${box} messages`} mobileTitle={(row) => row.subject} />
        <TableLoadingOverlay loading={loading} /></Box>
    </Card>

    <ResponsiveDialog open={compose} onClose={() => setCompose(false)} maxWidth="sm">
      <DialogTitle>New Internal Message</DialogTitle><DialogContent dividers><Stack spacing={2}>
        <TextField select label="To" value={form.recipient_admin_id} onChange={(e) => setForm({ ...form, recipient_admin_id: e.target.value })}>
          {recipients.map((recipient) => <MenuItem key={recipient.id} value={recipient.id}>{staffName(recipient)} - {recipient.role} ({recipient.email})</MenuItem>)}
        </TextField>
        <TextField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} inputProps={{ maxLength: 180 }} />
        <TextField label="Message" multiline minRows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} inputProps={{ maxLength: 5000 }} />
      </Stack></DialogContent><DialogActions><Button onClick={() => setCompose(false)}>Cancel</Button>
        <Button variant="contained" disabled={!form.recipient_admin_id || !form.subject.trim() || !form.message.trim()} onClick={sendInternal}>Send</Button></DialogActions>
    </ResponsiveDialog>

    <ResponsiveDialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm">
      <DialogTitle>{selected?.subject}</DialogTitle><DialogContent dividers>
        <Typography><strong>From:</strong> {staffName(selected?.Sender)} ({selected?.Sender?.role})</Typography>
        <Typography><strong>To:</strong> {staffName(selected?.Recipient)} ({selected?.Recipient?.role})</Typography>
        <Typography><strong>Date:</strong> {selected?.created_at ? formatAdminDateTime(selected.created_at) : ""}</Typography>
        <Typography sx={{ mt: 2, whiteSpace: "pre-wrap" }}>{selected?.message}</Typography>
      </DialogContent><DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
    </ResponsiveDialog>

    <ResponsiveDialog open={Boolean(transaction)} onClose={() => { setTransaction(null); setSearchParams({}); }} maxWidth="sm">
      <DialogTitle>Email Customer About Transaction</DialogTitle><DialogContent dividers><Stack spacing={1}>
        <Typography><strong>Customer:</strong> {transaction?.customer?.name} ({transaction?.customer?.profile_id})</Typography>
        <Typography><strong>Email:</strong> {transaction?.customer?.email}</Typography><Typography><strong>Reference:</strong> {transaction?.reference}</Typography>
        <TextField label="Email message" multiline minRows={6} value={transaction?.message || ""}
          onChange={(e) => setTransaction({ ...transaction, message: e.target.value })} inputProps={{ maxLength: 5000 }} />
      </Stack></DialogContent><DialogActions><Button onClick={() => { setTransaction(null); setSearchParams({}); }}>Cancel</Button>
        <Button variant="contained" disabled={!transaction?.message?.trim()} onClick={sendCustomerEmail}>Queue Email</Button></DialogActions>
    </ResponsiveDialog>
  </Box>;
}
